/* eslint-disable no-underscore-dangle */
import axios, { AxiosError, AxiosResponse, AxiosRequestConfig } from 'axios';
import { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import config from '../config';
import { isRunningInIFrame, normalizeRequestUrl } from './functionHelpers';

type AxiosRequestInterceptor = [
	(requestConfig: AxiosRequestConfig) => Promise<AxiosRequestConfig>,
	(error: AxiosError) => Promise<AxiosError> | void
];

type AxiosResponseInterceptor = [
	(response: AxiosResponse) => AxiosResponse,
	(error: AxiosError) => Promise<AxiosError> | void
];

const inflightMutations = new Set<symbol>();

const trackMutationRequest = (cfg: AxiosRequestConfig): AxiosRequestConfig => {
	if (['put', 'post', 'patch', 'delete'].includes((cfg.method || '').toLowerCase())) {
		const sentinel = Symbol('inflightSentinel');
		inflightMutations.add(sentinel);
		return { ...cfg, __inflightSentinel: sentinel } as AxiosRequestConfig;
	}
	return cfg;
};

// Waits for all in-flight mutation requests to settle, then resolves.
// Falls through after timeoutMs to avoid blocking the camera handoff indefinitely.
export const waitForInflightMutations = async (timeoutMs = 3000): Promise<void> => {
	const deadline = Date.now() + timeoutMs;
	while (inflightMutations.size > 0 && Date.now() < deadline) {
		// eslint-disable-next-line no-await-in-loop
		await new Promise(r => setTimeout(r, 50));
	}
};

// Exposed only for unit tests — clears in-flight state so each test starts clean.
export const _resetInflightMutationsForTesting = (): void => inflightMutations.clear();

const ACCESS_TOKEN = 'ACCESS_TOKEN';
const EXPIRATION_TIME_MS = 'EXPIRATION_TIME_MS';

const isTinEndpointUrl = (url?: string) => {
	if (!url) return false;

	return url.includes('tin/');
};

const isTin404Error = (error: AxiosError) => {
	const requestUrl = error.config?.url || error.response?.config?.url;

	return error.response?.status === 404 && isTinEndpointUrl(requestUrl);
};

const axiosResponseInterceptor: AxiosResponseInterceptor = [
	(response: AxiosResponse) => {
		const sentinel = (response.config as any).__inflightSentinel;
		if (sentinel) inflightMutations.delete(sentinel);
		return response;
	},
	(error: AxiosError) => {
		const sentinel = error.config && (error.config as any).__inflightSentinel;
		if (sentinel) inflightMutations.delete(sentinel);
		const isExpectedTin404 = isTin404Error(error);
		sessionStorage.removeItem('traceId');
		if (
			error.response &&
			error.response.status === 401 &&
			window.isAuthenticated &&
			!window.isRedirecting &&
			isRunningInIFrame()
		) {
			window.isRedirecting = true;
			window.parent.postMessage({ event: 'force_redirect' }, '*');
		}

		if (error.config && !isExpectedTin404) {
			Sentry.setExtra('HTTP Request Data', {
				baseURL: error.config.baseURL,
				url: error.config.url,
				headers: error.config.headers,
				method: error.config.method,
				withCredentials: error.config.withCredentials
			});
			Sentry.setTag('request_url', error.config.url);
		}

		if (error.response) {
			const { response } = error;
			if (!isExpectedTin404) {
				Sentry.setExtra('HTTP Response Data', {
					data: response.data,
					headers: response.headers,
					status: response.status,
					statusText: response.statusText || null
				});
			}
			if (response.data?.traceId) {
				sessionStorage.setItem('traceId', response.data.traceId);
				if (!isExpectedTin404) {
					Sentry.setTag('traceId', response.data.traceId);

					const errorKey = response.data?.key;
					if (errorKey) {
						Sentry.captureException(new Error(`Network error: ${errorKey}`));
					} else {
						Sentry.captureException(new Error('Network error'));
					}
				}
			} else {
				// Non API error
				const cfRay = response.headers?.['cf-ray'];
				if (cfRay) {
					const cfTraceId = `CF-${cfRay}`;
					Sentry.setTag('traceId', cfTraceId);
					sessionStorage.setItem('traceId', cfTraceId);
				}
				Sentry.captureException(new Error('Middleware error (Cloudflare, nginx, etc.)'));
			}
			window.dispatchEvent(new Event('storage'));
		} else if (error.request) {
			if (!isExpectedTin404) {
				const method = (error.config?.method ?? 'UNKNOWN').toUpperCase();
				const normalizedUrl = normalizeRequestUrl(error.config?.url);
				Sentry.withScope(scope => {
					scope.setFingerprint(['request-blocked', method, normalizedUrl]);
					Sentry.captureException(
						new Error(
							`Request blocked or no response received: ${method} ${normalizedUrl}`
						)
					);
				});
			}
		} else {
			// Disabled until issue KYC-5509 is resolved
			// Sentry.captureException(new Error('Unknown network error'));
		}

		return Promise.reject(error);
	}
];

export default () => {
	const tokenRef = useRef(axios.CancelToken.source());
	const axiosRef = useRef(
		(() => {
			const axiosInstance = axios.create({
				cancelToken: tokenRef.current.token,
				baseURL: config.apiBaseUrl,
				withCredentials: true
			});

			const axiosRequestInterceptor: AxiosRequestInterceptor = [
				async requestConfig => {
					if (requestConfig.url === '/proxy/client_login') {
						return requestConfig;
					}

					const token = sessionStorage.getItem(ACCESS_TOKEN);
					const expTime = sessionStorage.getItem(EXPIRATION_TIME_MS);
					if (token && expTime) {
						const currentTime = Date.now();
						const expirationTime = parseInt(expTime, 10);

						if (currentTime >= expirationTime) {
							try {
								const { data } = await axiosInstance.post('/proxy/client_login');
								const { access_token: accessToken, exp } = data;
								const newToken = accessToken;
								const expMs = exp * 1000;

								sessionStorage.setItem(ACCESS_TOKEN, newToken);
								sessionStorage.setItem(EXPIRATION_TIME_MS, String(expMs));

								return {
									...requestConfig,
									headers: {
										...requestConfig.headers,
										Authorization: `Bearer ${newToken}`
									}
								};
							} catch (error) {
								return Promise.reject(error);
							}
						} else {
							return {
								...requestConfig,
								headers: {
									...requestConfig.headers,
									Authorization: `Bearer ${token}`
								}
							};
						}
					}

					return requestConfig;
				},
				error => Promise.reject(error)
			];

			axiosInstance.interceptors.request.use(trackMutationRequest);
			axiosInstance.interceptors.request.use(...axiosRequestInterceptor);

			axiosInstance.interceptors.response.use(...axiosResponseInterceptor);

			return axiosInstance;
		})()
	);

	useEffect(() => tokenRef.current.cancel, []);

	return axiosRef.current;
};
